import React, { useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CloseFormShowButton, EditFormShowButton, SaveFormShowButton, useFormShowContext } from 'framework/form';
import { ShowSection } from 'framework/ui/layout';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS, useProjectSecurityContext } from 'domain/shared/newProject';

interface EngineeringSectionProps {
  title: string;
  editContent: React.ReactNode;
  viewContent: React.ReactNode;
}

const EngineeringSection = ({ title, editContent, viewContent }: EngineeringSectionProps) => {
  const { isEditMode } = useFormShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const canManage = isAccessGranted(ACCESS_INFO_SECTIONS.ENGINEERING, ACCESS_INFO_ACTIONS.MANAGE);

  const actions = useCallback(() => {
    if (!canManage) return null;
    return isEditMode
      ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
      : [<EditFormShowButton key="edit" />];
  }, [isEditMode, canManage]);

  return (
    <ShowSection
      title={
        <Typography variant="h4" color="primary">
          {title}
        </Typography>
      }
      justifyContent="space-between"
      sx={{
        '.show-title-container': { mb: 0, py: 1, pl: 2, pr: 0, display: 'flex' },
        '.show-title-container > div': { alignItems: 'center', display: 'flex', gap: 1 },
        '&.section-details__wrapper': { p: '0px !important', width: '100%' },
        '& .section-details__actions': { mt: 0 },
      }}
      actions={actions()}
    >
      <Box sx={{ height: '1px', bgcolor: 'border.default', mb: 2 }} />
      <Box sx={{ px: 2 }}>
        {isEditMode ? (
          <Stack spacing={2} sx={{ pb: 2 }}>
            {editContent}
          </Stack>
        ) : (
          <Stack spacing={0.5} sx={{ pb: 2 }}>
            {viewContent}
          </Stack>
        )}
      </Box>
    </ShowSection>
  );
};

export default EngineeringSection;
